import {
  GET_COUNTRY_CONFIG,
  GET_PHONE_NUMBER_IN_SIGNUP_US_VARIANT,
} from '@/components/constants/flags';
import type { CtaConfig, CtaSelector, PhoneNumberVariant } from '@/types/country-config';

export interface ResolvedCtaConfig {
  primary: CtaSelector;
  fallback?: CtaSelector;
}

const getPhoneExperimentOverride = (countryCode: string): PhoneNumberVariant | null => {
  let variant: PhoneNumberVariant;
  switch (countryCode) {
    case 'US':
      variant = GET_PHONE_NUMBER_IN_SIGNUP_US_VARIANT();
      break;
    case 'DE':
      // Shipped: release-phone-number-in-signup-de at 100% → optional
      return 'optional';
    // ZA: release-phone-number-in-signup-za shipped at 100% → hidden (no experiment override)
    default:
      return null;
  }
  // Only override when the flag is actively set to a non-hidden variant
  return variant !== 'hidden' ? variant : null;
};

export const getPhoneNumberVariantForCountry = (
  countryCode: string | undefined
): PhoneNumberVariant => {
  if (!countryCode) return 'hidden';

  // Per-country experiment flags take precedence (for A/B testing)
  const experimentOverride = getPhoneExperimentOverride(countryCode);
  if (experimentOverride) return experimentOverride;

  // Steady-state config from PostHog country-config payload
  const config = GET_COUNTRY_CONFIG();
  if (config) {
    return config[countryCode]?.phoneNumber ?? 'hidden';
  }

  return 'hidden';
};

const resolveLanguageValue = <T>(
  value: T | Record<string, T> | undefined,
  languageCode: string | null | undefined
): T | null => {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'object') return value;
  const map = value as Record<string, T>;
  if (languageCode && map[languageCode] !== undefined) return map[languageCode];
  return null;
};

const resolveLanguageLink = (
  link: string | Record<string, string> | undefined,
  languageCode: string | null | undefined
): string | null => resolveLanguageValue(link, languageCode);

export const getMeetingLinkForCountry = (
  countryCode: string | undefined,
  languageCode?: string | null
): string | null => {
  const config = GET_COUNTRY_CONFIG();
  if (!config || !countryCode) return null;
  return resolveLanguageLink(config[countryCode]?.meetingLink, languageCode);
};

export const getDiscoveryCallLinkForCountry = (
  countryCode: string | undefined,
  languageCode?: string | null
): string | null => {
  const config = GET_COUNTRY_CONFIG();
  if (!config || !countryCode) return null;
  return resolveLanguageLink(config[countryCode]?.discoveryCallLink, languageCode);
};

export const getLiveDemoLinkForCountry = (
  countryCode: string | undefined,
  languageCode?: string | null
): string | null => {
  const config = GET_COUNTRY_CONFIG();
  if (!config || !countryCode) return null;
  return resolveLanguageLink(config[countryCode]?.liveDemoLink, languageCode);
};

export const getLiveDemoMaxAccountAgeDays = (
  countryCode: string | undefined
): number | null => {
  if (!countryCode) return null;
  const config = GET_COUNTRY_CONFIG();
  return config?.[countryCode]?.liveDemoMaxAccountAgeDays ?? null;
};

const normalizeCtaConfig = (value: CtaConfig): ResolvedCtaConfig =>
  typeof value === 'string' ? { primary: value } : value;

const isCtaConfigObject = (
  value: object
): value is { primary: CtaSelector; fallback?: CtaSelector } => 'primary' in value;

export const getOnboardingCtaForCountry = (
  countryCode: string | undefined,
  languageCode?: string | null
): ResolvedCtaConfig | null => {
  if (!countryCode) return null;
  const config = GET_COUNTRY_CONFIG();
  if (!config) return null;
  const value = config[countryCode]?.onboardingCta;
  if (value === undefined) return null;

  // Bare CtaSelector string at the top level.
  if (typeof value === 'string') return normalizeCtaConfig(value);

  // Single-config object form: { primary, fallback? }.
  if (isCtaConfigObject(value)) return normalizeCtaConfig(value);

  // Per-language map: { de: ..., fr: ... }.
  if (languageCode) {
    const langValue = value[languageCode];
    if (langValue !== undefined) return normalizeCtaConfig(langValue);
  }
  return null;
};

/**
 * Returns the ISO alpha-2 country code whose `country-config` entry has been
 * allotted to the user, or null when the payload has no explicit entry for that
 * country (default behaviour). The country code itself is the config key, so it
 * uniquely identifies which config was applied — used for analytics segmentation.
 */
export const getAllottedCountryConfigKey = (
  countryCode: string | undefined
): string | null => {
  if (!countryCode) return null;
  const config = GET_COUNTRY_CONFIG();
  return config?.[countryCode] ? countryCode : null;
};

/**
 * Resolved `country-config` fields shaped for analytics. Records what a user is
 * shown for a given country: which config key was allotted, the phone-field
 * state, and the configured onboarding CTA (primary + fallback).
 */
export interface CountryConfigEventProps {
  country_config: string | null;
  phone_number_variant: PhoneNumberVariant;
  onboarding_cta_primary?: CtaSelector;
  onboarding_cta_fallback?: CtaSelector;
}

/**
 * Builds the `country-config` analytics props for the given country (and
 * optional display language, used to resolve per-language CTA maps). Pass the
 * best-available country code at the call site — the user-selected country if
 * chosen, else the timezone-detected one — so the event reflects what the user
 * was actually shown at that moment.
 */
export const getCountryConfigEventProps = (
  countryCode: string | undefined,
  languageCode?: string | null
): CountryConfigEventProps => {
  const cta = getOnboardingCtaForCountry(countryCode, languageCode);
  return {
    country_config: getAllottedCountryConfigKey(countryCode),
    phone_number_variant: getPhoneNumberVariantForCountry(countryCode),
    onboarding_cta_primary: cta?.primary,
    onboarding_cta_fallback: cta?.fallback,
  };
};

export const isWebinarEnabledForCountry = (countryCode: string | undefined): boolean => {
  if (!countryCode) return false;
  const config = GET_COUNTRY_CONFIG();
  if (!config) return true; // schema default applies when payload absent
  const entry = config[countryCode];
  if (!entry) return true; // unknown country still gets webinar (schema default)
  return entry.webinar ?? true;
};
